/**
 * Service Provider and Telemetry Connector options for the dashboard sidebar.
 *
 * Ported from the legacy Vue app's `items` array (SideBarDashboard/Cloud.vue),
 * which hardcodes both lists — there is no endpoint behind either control. The
 * title strings are byte-identical to legacy because they travel to the backend
 * as provider identifiers.
 *
 * Same shape as legacy: ONE "Service Provider" dropdown whose menu has two
 * side-by-side columns, "Cloud" | "Telemetry Connector" (an earlier version
 * split them into two fields; users preferred legacy's single picker).
 *
 * Picking a connector still needs a CLOUD behind it — the Region list, the
 * templates and Add Instance all work per cloud. TELEMETRY_CLOUDS says which:
 * one fixed cloud (CloudWatch -> AWS), or a choice the user makes in a second
 * "Cloud" field (Datadog, Prometheus, Billing), as legacy's `Cloud*` field.
 *
 * THE COMBINATIONS (all from legacy Cloud.vue — line refs there):
 *
 *  1. Telemetry Connector column in the menu (:110-113)
 *       shown  — model SPEC CPU and the pick is AWS / AZURE / GCP / a connector
 *       hidden — the pick is OCI, or the model is General IT Average (CCA only)
 *
 *  2. Connectors offered (:258-273)
 *       CCA — Billing, Datadog, AWS CloudWatch, Azure App Insights,
 *             Google Cloud Ops Agent, Prometheus
 *       EIA — the same without Billing
 *
 *  3. Second "Cloud" field (:177-182) — only when the connector works on more
 *     than one cloud, so there is something to choose:
 *       Datadog, Prometheus, Billing  -> shown: AWS / AZURE / GCP (starts on AWS)
 *       AWS CloudWatch                -> hidden: always AWS
 *       Azure App Insights            -> hidden: always AZURE
 *       Google Cloud Ops Agent        -> hidden: always GCP
 *       a cloud (AWS…OCI)             -> hidden: that cloud
 *     OCI is never a Cloud choice — no connector supports it.
 *
 *  4. What a pick clears (slice.js)
 *       cloud      -> drops any connector (and the Cloud field)
 *       connector  -> sets its cloud (the only one, or AWS)
 *       General IT Average -> drops the connector, keeps its cloud
 *       any Service Provider / Cloud change -> clears the portfolio search
 *       and the Add Instance form
 *
 * Neither field is marked required (*): each always has a value, so there is
 * nothing for the user to fill in. Legacy marks only Benchmark / Model.
 */
import awsIcon from '@/assets/providers/awslogo.svg';
import azureInsightsIcon from '@/assets/providers/azureInsights.svg';
import azureIcon from '@/assets/providers/azurelogo.svg';
import billingIcon from '@/assets/providers/Billing.png';
import cloudWatchIcon from '@/assets/providers/cloudWatch.svg';
import datadogIcon from '@/assets/providers/datadog.svg';
import googleIcon from '@/assets/providers/googlelogo.svg';
import ociIcon from '@/assets/providers/ocilogo.svg';
import prometheusIcon from '@/assets/providers/prometheus.svg';

import { BENCHMARK_MODELS } from './benchmarkModels';

/** Cloud service providers. Same four in both products. */
export const CLOUD_PROVIDERS = Object.freeze(['AWS', 'AZURE', 'GCP', 'OCI']);

export const DEFAULT_CLOUD_PROVIDER = 'AWS';

/** The provider that suppresses telemetry entirely. */
export const TELEMETRY_UNSUPPORTED_PROVIDER = 'OCI';

export const TELEMETRY_CONNECTORS = Object.freeze([
  'Billing',
  'Datadog',
  'AWS CloudWatch',
  'Azure App Insights',
  'Google Cloud Ops Agent',
  'Prometheus',
]);

/**
 * The cloud(s) each connector can sit on — legacy Cloud.vue `telemetryOptions`
 * (:279-286). The first is the default when the connector is picked. Legacy
 * spells App Insights' cloud 'Azure'; the provider id everywhere else is
 * 'AZURE', so that is used here.
 */
export const TELEMETRY_CLOUDS = Object.freeze({
  Billing: Object.freeze(['AWS', 'AZURE', 'GCP']),
  Datadog: Object.freeze(['AWS', 'AZURE', 'GCP']),
  'AWS CloudWatch': Object.freeze(['AWS']),
  'Azure App Insights': Object.freeze(['AZURE']),
  'Google Cloud Ops Agent': Object.freeze(['GCP']),
  Prometheus: Object.freeze(['AWS', 'AZURE', 'GCP']),
});

/**
 * Connector -> its Telemetry page (`/telemetry?type=<slug>`), legacy's
 * TELEMETRY_NAME_MAP (utils/telemetryConfig.js:204-211) and the `type` the
 * /telemetry/* APIs take. Billing is NOT here: picking it keeps Manage
 * Portfolio (CUR-report upload, legacy Cloud.vue :513-520), it has no form.
 */
export const TELEMETRY_SLUGS = Object.freeze({
  Datadog: 'datadog',
  'AWS CloudWatch': 'cloudwatch',
  'Azure App Insights': 'azureinsights',
  'Google Cloud Ops Agent': 'gcptelemetry',
  Prometheus: 'prometheus',
});

/**
 * The CCA-only "Billing" connector: CUR-report portfolios. It is not a
 * telemetry page — picking it keeps Manage Portfolio in Billing (CUR) mode
 * (legacy fileType 'curreports', Cloud.vue :513-520).
 */
export const BILLING_CONNECTOR = 'Billing';

/**
 * Is Manage Portfolio in Billing (CUR) mode? Takes the connector itself or a
 * `{ telemetry }` selection (the sidebar's selectSelectedTelemetry).
 * Legacy: `selectedCloudService === 'Billing'`.
 *
 * @param {string | { telemetry?: string } | null | undefined} selection
 */
export function isBillingMode(selection) {
  const telemetry = typeof selection === 'string' ? selection : selection?.telemetry;
  return telemetry === BILLING_CONNECTOR;
}

/**
 * The `provider` a Manage Portfolio request sends. In Billing mode that is the
 * cloud picked in the "Cloud" field (legacy `datadogCSP`: FooterActions.vue
 * :264, :329; UploadDownloadTemplate.vue :408 getCloudForTelemetry), never the
 * word "Billing"; a cloud Billing does not offer falls back to its first (AWS),
 * as legacy's field starts there. Outside Billing: the provider as given.
 *
 * The slice already keeps `selectedProvider` as the cloud behind any connector,
 * so with sidebar state this is normally the provider unchanged — it is the
 * guard that "Billing" itself can never reach the server as a provider.
 *
 * @param {{ telemetry?: string, provider?: string }} selection
 * @returns {string | undefined}
 */
export function billingProvider({ telemetry, provider } = {}) {
  if (!isBillingMode(telemetry)) return provider;
  const clouds = cloudsForTelemetry(BILLING_CONNECTOR);
  return clouds.includes(provider) ? provider : clouds[0];
}

/** The Telemetry page slug for a connector, or null (a cloud, Billing, nothing). */
export function telemetrySlug(connector) {
  return TELEMETRY_SLUGS[connector] ?? null;
}

/** The connector a slug stands for (from the address), or null. */
export function connectorForSlug(slug) {
  const key = String(slug ?? '').toLowerCase();
  return Object.keys(TELEMETRY_SLUGS).find((c) => TELEMETRY_SLUGS[c] === key) ?? null;
}

/** Is this a cloud (left column) rather than a telemetry connector? */
export function isCloudProvider(value) {
  return CLOUD_PROVIDERS.includes(value);
}

/** The clouds a connector can sit on ([] for an unknown one). */
export function cloudsForTelemetry(connector) {
  return TELEMETRY_CLOUDS[connector] ?? [];
}

/**
 * Does this connector need the extra "Cloud" field? Only when it can sit on
 * more than one cloud — legacy shows `Cloud*` for exactly Datadog, Prometheus
 * and Billing (Cloud.vue:177-178).
 */
export function needsCloudChoice(connector) {
  return cloudsForTelemetry(connector).length > 1;
}

/**
 * Logos, copied from the legacy app's src/assets/.
 *
 * `googlelogo.svg` is deliberately shared by GCP and Google Cloud Ops Agent,
 * as in legacy. Billing is the one PNG.
 *
 * Legacy additionally applies `filter: brightness(0)` to the AWS mark, which
 * flattens it to solid black — correct on its light Vuetify theme, invisible on
 * this app's black surfaces. Not ported, same call as the SPEC CPU icon.
 */
export const PROVIDER_ICONS = Object.freeze({
  AWS: awsIcon,
  AZURE: azureIcon,
  GCP: googleIcon,
  OCI: ociIcon,
  Billing: billingIcon,
  Datadog: datadogIcon,
  'AWS CloudWatch': cloudWatchIcon,
  'Azure App Insights': azureInsightsIcon,
  'Google Cloud Ops Agent': googleIcon,
  Prometheus: prometheusIcon,
});

/**
 * The telemetry connectors this product exposes.
 *
 * `Billing` is CCA-only, matching legacy's `isCCA() ? [...] : [...]` split.
 *
 * @param {'cca' | 'eia'} mode
 * @returns {string[]}
 */
export function getTelemetryConnectors(mode) {
  return mode === 'cca'
    ? [...TELEMETRY_CONNECTORS]
    : TELEMETRY_CONNECTORS.filter((c) => c !== 'Billing');
}

/**
 * Why telemetry is unavailable right now, or null if it is available.
 *
 * Under either condition the Telemetry Connector column is left out of the
 * Service Provider menu, as legacy does (Cloud.vue:110-113).
 *
 * @param {{ provider: string, model: string }} selection
 * @returns {string | null}
 */
export function getTelemetryUnavailableReason({ provider, model }) {
  if (provider === TELEMETRY_UNSUPPORTED_PROVIDER) {
    return `Telemetry Connector is not available for ${TELEMETRY_UNSUPPORTED_PROVIDER}`;
  }
  if (model === BENCHMARK_MODELS.GENERAL_IT_AVERAGE) {
    return `Telemetry Connector is not available for ${BENCHMARK_MODELS.GENERAL_IT_AVERAGE}`;
  }
  return null;
}

/**
 * The third-party-logo notice legacy pins under the menu. Copy verbatim.
 *
 * Split into heading and body because legacy bolds the heading — note the
 * colon sits INSIDE the <strong>, so it is part of the label string.
 */
export const PROVIDER_DISCLAIMER_LABEL = 'Disclaimer:';

export const PROVIDER_DISCLAIMER =
  'All third-party logos and icons used are the property of their respective ' +
  'owners and are displayed for informational purposes only, without implying ' +
  'any affiliation, endorsement, or sponsorship.';
